// Performance API service
import { baseClient } from './baseClient';
import type { UserPerformance } from '../types/api';

export class PerformanceApi {
  private static instance: PerformanceApi;

  private constructor() {}

  public static getInstance(): PerformanceApi {
    if (!PerformanceApi.instance) {
      PerformanceApi.instance = new PerformanceApi();
    }
    return PerformanceApi.instance;
  }

  /**
   * Get all user performance data
   */
  async getUserPerformance(): Promise<UserPerformance[]> {
    return baseClient.get<UserPerformance[]>('/team');
  }

  /**
   * Get performance data for a specific user
   */
  async getUserPerformanceById(userId: number): Promise<UserPerformance[]> {
    return baseClient.get<UserPerformance[]>(`/performance/users/${userId}`);
  }

  /**
   * Get performance data by period
   */
  async getPerformanceByPeriod(period: string): Promise<UserPerformance[]> {
    return baseClient.get<UserPerformance[]>(`/performance/periods/${period}`);
  }

  /**
   * Get current period performance
   */
  async getCurrentPeriodPerformance(): Promise<UserPerformance[]> {
    return baseClient.get<UserPerformance[]>('/performance/current');
  }

  /**
   * Get performance trends
   */
  async getPerformanceTrends(periods: number = 12): Promise<any> {
    return baseClient.get<any>(`/performance/trends?periods=${periods}`);
  }

  /**
   * Get team performance summary
   */
  async getTeamPerformanceSummary(): Promise<any> {
    return baseClient.get<any>('/performance/team-summary');
  }

  /**
   * Get individual performance metrics
   */
  async getIndividualMetrics(userId: number): Promise<any> {
    return baseClient.get<any>(`/performance/users/${userId}/metrics`);
  }

  /**
   * Get performance leaderboard
   */
  async getPerformanceLeaderboard(metric: string = 'quota_attainment', limit: number = 10): Promise<any[]> {
    return baseClient.get<any[]>(`/performance/leaderboard?metric=${metric}&limit=${limit}`);
  }

  /**
   * Get quota attainment data
   */
  async getQuotaAttainment(): Promise<any[]> {
    return baseClient.get<any[]>('/performance/quota-attainment');
  }

  /**
   * Get sales velocity metrics
   */
  async getSalesVelocity(): Promise<any> {
    return baseClient.get<any>('/performance/sales-velocity');
  }

  /**
   * Get win rate analysis
   */
  async getWinRateAnalysis(): Promise<any> {
    return baseClient.get<any>('/performance/win-rate');
  }

  /**
   * Get average deal size trends
   */
  async getAverageDealSizeTrends(): Promise<any> {
    return baseClient.get<any>('/performance/average-deal-size');
  }

  /**
   * Get sales cycle length analysis
   */
  async getSalesCycleAnalysis(): Promise<any> {
    return baseClient.get<any>('/performance/sales-cycle');
  }

  /**
   * Get performance comparison
   */
  async getPerformanceComparison(userIds: number[]): Promise<any> {
    const params = new URLSearchParams();
    userIds.forEach(id => params.append('user_ids', id.toString()));
    
    return baseClient.get<any>(`/performance/comparison?${params.toString()}`);
  }

  /**
   * Get performance goals
   */
  async getPerformanceGoals(userId?: number): Promise<any[]> {
    const url = userId 
      ? `/performance/goals/users/${userId}`
      : '/performance/goals';
    return baseClient.get<any[]>(url);
  }

  /**
   * Set performance goals
   */
  async setPerformanceGoals(userId: number, goals: any): Promise<any> {
    return baseClient.post<any>(`/performance/goals/users/${userId}`, goals);
  }

  /**
   * Get performance alerts
   */
  async getPerformanceAlerts(): Promise<any[]> {
    return baseClient.get<any[]>('/performance/alerts');
  }

  /**
   * Get performance reports
   */
  async getPerformanceReports(reportType: string, period?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('type', reportType);
    if (period) params.append('period', period);
    
    return baseClient.get<any>(`/performance/reports?${params.toString()}`);
  }

  /**
   * Export performance data
   */
  async exportPerformanceData(format: 'csv' | 'excel' = 'csv', filters?: any): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }
    
    const response = await baseClient.get<Blob>(`/performance/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response;
  }

  /**
   * Get performance dashboard data
   */
  async getPerformanceDashboard(): Promise<any> {
    return baseClient.get<any>('/performance/dashboard');
  }

  /**
   * Get performance insights
   */
  async getPerformanceInsights(): Promise<any[]> {
    return baseClient.get<any[]>('/performance/insights');
  }

  /**
   * Get performance predictions
   */
  async getPerformancePredictions(): Promise<any> {
    return baseClient.get<any>('/performance/predictions');
  }

  /**
   * Get performance benchmarks
   */
  async getPerformanceBenchmarks(): Promise<any> {
    return baseClient.get<any>('/performance/benchmarks');
  }

  /**
   * Get performance coaching recommendations
   */
  async getCoachingRecommendations(userId: number): Promise<any[]> {
    return baseClient.get<any[]>(`/performance/coaching/${userId}`);
  }

  /**
   * Get performance improvement suggestions
   */
  async getImprovementSuggestions(userId: number): Promise<any[]> {
    return baseClient.get<any[]>(`/performance/improvements/${userId}`);
  }
}

// Export singleton instance
export const performanceApi = PerformanceApi.getInstance(); 